import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DatePicker, Input, Select } from 'antd';
import { FilterBar } from '../../../components/list';
import type { ClosingStatus } from '../types';
import { CLOSING_STATUS_OPTIONS } from '../constants';
import { useLeadPipeline } from '../hooks/useLeadPipeline';
import { useLeadSources } from '../hooks/useLeadSources';
import { useContactCategories } from '../../contacts/hooks/useContactCategories';
import { useContactOptions } from '../../../hooks/options/useContactOptions';
import ContactSearchSelect from '../../contacts/components/ContactSearchSelect';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useDebounce } from '../../../hooks/useDebounce';

const { Search } = Input;
const { RangePicker } = DatePicker;

export interface LeadFilterValues {
  search?: string;
  pipelineStageId?: number;
  closingStatus?: ClosingStatus;
  assigneeUserId?: string;
  contactIds?: string[];
  contactCategoryId?: number;
  sourceId?: number;
  campaignId?: string;
  referralContactId?: string;
  salesPersonId?: string;
  createdRange?: [Dayjs | null, Dayjs | null] | null;
  createdFrom?: string;
  createdTo?: string;
  segmentIds?: string[];
  hasReferral?: boolean;
}

export interface LeadFilterBarProps {
  value: LeadFilterValues;
  onChange: (value: LeadFilterValues) => void;
  onReset: () => void;
  assigneeOptions: { value: string; label: string }[];
  campaignOptions: { value: string; label: string }[];
  segmentOptions?: { value: string; label: string }[];
  loadingAssignees?: boolean;
}

const LeadFilterBar: React.FC<LeadFilterBarProps> = ({
  value,
  onChange,
  onReset,
  assigneeOptions,
  campaignOptions,
  segmentOptions = [],
  loadingAssignees = false,
}) => {
  const { data: pipeline } = useLeadPipeline();
  const { data: sources } = useLeadSources();
  const { data: categories } = useContactCategories();
  const stageOptions = (pipeline?.stages ?? [])
    .filter((s) => s.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((s) => ({ value: s.id, label: s.label }));
  const sourceOptions = sources?.filter(s => s.isActive).map(s => ({ value: s.id, label: s.label })) ?? [];
  const categoryOptions = categories?.filter(c => c.isActive).map(c => ({ value: c.id, label: c.label })) ?? [];
  const { data: referralContacts = [] } = useContactOptions({ role: 0 });
  const referralContactOptions = referralContacts.map(c => ({
    value: c.id,
    label: c.label,
  }));
  const { data: salesPersons = [] } = useContactOptions({ role: 1 });
  const salesPersonOptions = salesPersons.map(c => ({
    value: c.id,
    label: c.label,
  }));

  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  useLayoutEffect(() => { valueRef.current = value; }, [value]);
  useLayoutEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  const lastCommittedRef = useRef(value.search ?? '');

  const [localSearch, setLocalSearch] = useState(value.search ?? '');
  const debouncedSearch = useDebounce(localSearch, 300);

  // Sync debounced value to parent (passive debounce — fires ~300ms after typing stops)
  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    if (trimmed === lastCommittedRef.current) return;
    lastCommittedRef.current = trimmed;
    onChangeRef.current({ ...valueRef.current, search: trimmed || undefined });
  }, [debouncedSearch]);

  // Sync from parent (e.g., external reset)
  useEffect(() => {
    setLocalSearch(value.search ?? '');
  }, [value.search]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setLocalSearch(val);
    if (val === '') {
      // Clear (X) button — fire immediately
      if (lastCommittedRef.current === '') return;
      lastCommittedRef.current = '';
      onChangeRef.current({ ...valueRef.current, search: undefined });
    }
  };

  const handleSearch = (v: string) => {
    // Enter key or search button — fire immediately (no debounce)
    const trimmed = v.trim();
    setLocalSearch(v);
    if (trimmed === lastCommittedRef.current) return;
    lastCommittedRef.current = trimmed;
    onChangeRef.current({ ...valueRef.current, search: trimmed || undefined });
  };

  const handleStageChange = (v: number | undefined) => {
    onChange({ ...value, pipelineStageId: v });
  };
  const handleClosingStatusChange = (v: ClosingStatus | undefined) => {
    onChange({ ...value, closingStatus: v });
  };
  const handleAssigneeChange = (v: string | undefined) => {
    onChange({ ...value, assigneeUserId: v });
  };
  const handleContactChange = (v: string[]) => {
    onChange({ ...value, contactIds: v.length > 0 ? v : undefined });
  };
  const handleContactCategoryChange = (v: number | undefined) => {
    onChange({ ...value, contactCategoryId: v });
  };
  const handleSourceChange = (v: number | undefined) => {
    onChange({ ...value, sourceId: v });
  };
  const handleCampaignChange = (v: string | undefined) => {
    onChange({ ...value, campaignId: v });
  };
  const handleReferralContactChange = (v: string | undefined) => {
    onChange({ ...value, referralContactId: v });
  };
  const handleSalesPersonChange = (v: string | undefined) => {
    onChange({ ...value, salesPersonId: v });
  };
  const handleSegmentChange = (v: string[]) => {
    onChange({ ...value, segmentIds: v.length > 0 ? v : undefined });
  };
  const handleHasReferralChange = (v: boolean | undefined) => {
    onChange({ ...value, hasReferral: v });
  };
  const handleRangeChange = (range: [Dayjs | null, Dayjs | null] | null) => {
    onChange({ ...value, createdRange: range });
  };

  const rangeValue = useMemo(() => {
    if (value.createdRange) return value.createdRange;
    if (value.createdFrom && value.createdTo) {
      return [dayjs(value.createdFrom), dayjs(value.createdTo)] as [Dayjs, Dayjs];
    }
    return undefined;
  }, [value.createdRange, value.createdFrom, value.createdTo]);

  const hasActiveFilters =
    Boolean(value.search) ||
    value.pipelineStageId !== undefined ||
    value.closingStatus !== undefined ||
    value.assigneeUserId !== undefined ||
    (value.contactIds !== undefined && value.contactIds.length > 0) ||
    value.contactCategoryId !== undefined ||
    value.sourceId !== undefined ||
    value.campaignId !== undefined ||
    value.referralContactId !== undefined ||
    value.salesPersonId !== undefined ||
    (value.createdRange !== undefined && value.createdRange !== null) ||
    (value.createdFrom !== undefined && value.createdFrom !== '') ||
    (value.createdTo !== undefined && value.createdTo !== '') ||
    (value.segmentIds !== undefined && value.segmentIds.length > 0) ||
    value.hasReferral !== undefined;

  const lbl: React.CSSProperties = { fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' };
  const box: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 2 };

  return (
    <FilterBar onReset={hasActiveFilters ? onReset : undefined}>
      <div style={box}>
        <span style={lbl}>Search</span>
        <Search
          allowClear
          onSearch={handleSearch}
          onChange={handleSearchChange}
          value={localSearch}
          style={{ width: 240 }}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Pipeline Stage</span>
        <Select<number>
          allowClear
          options={stageOptions}
          value={value.pipelineStageId}
          onChange={handleStageChange}
          style={{ width: 160 }}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Closing Status</span>
        <Select<ClosingStatus>
          allowClear
          options={CLOSING_STATUS_OPTIONS}
          value={value.closingStatus}
          onChange={handleClosingStatusChange}
          style={{ width: 140 }}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Assignee</span>
        <Select<string>
          allowClear
          options={assigneeOptions}
          value={value.assigneeUserId ?? undefined}
          onChange={handleAssigneeChange}
          loading={loadingAssignees}
          style={{ width: 180 }}
        />
      </div>
      {segmentOptions.length > 0 ? (
        <div style={box}>
          <span style={lbl}>Customer Segments</span>
          <Select<string[]>
            mode="multiple"
            allowClear
            options={segmentOptions}
            value={value.segmentIds}
            onChange={handleSegmentChange}
            style={{ minWidth: 200 }}
            optionFilterProp="label"
            maxTagCount="responsive"
          />
        </div>
      ) : null}
      <div style={box}>
        <span style={lbl}>Referral</span>
        <Select<string | undefined>
          allowClear
          style={{ width: 140 }}
          value={value.hasReferral === undefined ? undefined : String(value.hasReferral)}
          onChange={(v) => handleHasReferralChange(v === undefined ? undefined : v === 'true')}
          options={[
            { value: 'true', label: 'Referral' },
            { value: 'false', label: 'Non-referral' },
          ]}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Contact</span>
        <ContactSearchSelect
          value={value.contactIds}
          onChange={handleContactChange}
          style={{ minWidth: 220 }}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Contact Category</span>
        <Select<number>
          allowClear
          options={categoryOptions}
          value={value.contactCategoryId ?? undefined}
          onChange={handleContactCategoryChange}
          style={{ width: 180 }}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Source</span>
        <Select<number>
          allowClear
          options={sourceOptions}
          value={value.sourceId ?? undefined}
          onChange={handleSourceChange}
          style={{ width: 140 }}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Campaign</span>
        <Select<string>
          allowClear
          options={campaignOptions}
          value={value.campaignId ?? undefined}
          onChange={handleCampaignChange}
          style={{ width: 180 }}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Created Date</span>
        <RangePicker
          value={rangeValue}
          onChange={handleRangeChange}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Referral Contact</span>
        <Select<string>
          allowClear
          options={referralContactOptions}
          value={value.referralContactId ?? undefined}
          onChange={handleReferralContactChange}
          showSearch
          optionFilterProp="label"
          style={{ width: 180 }}
        />
      </div>
      <div style={box}>
        <span style={lbl}>Sales Person</span>
        <Select<string>
          allowClear
          options={salesPersonOptions}
          value={value.salesPersonId ?? undefined}
          onChange={handleSalesPersonChange}
          showSearch
          optionFilterProp="label"
          style={{ width: 180 }}
        />
      </div>
    </FilterBar>
  );
};

export default LeadFilterBar;
